import {
  BufferTarget,
  StreamTarget,
  type StreamTargetChunk
} from 'mediabunny';
import { throwIfRenderAborted } from './RenderTask';

const MEBIBYTE = 1024 * 1024;
const OPFS_DIRECTORY_NAME = 'motionpitch-video-exports';
const OPFS_FILE_PREFIX = 'motionpitch-video-export-';
const OPFS_CHUNK_SIZE = 4 * MEBIBYTE;
const OPFS_STALE_FILE_AGE_MS = 24 * 60 * 60 * 1000;
const OPFS_QUOTA_RESERVE_BYTES = 32 * MEBIBYTE;
const MAX_MEMORY_FALLBACK_BYTES = 256 * MEBIBYTE;

type StorageManagerWithOpfs = StorageManager & {
  getDirectory?: () => Promise<FileSystemDirectoryHandle>;
};

type DirectoryHandleWithEntries = FileSystemDirectoryHandle & {
  entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
};

type SaveFilePickerOptions = {
  suggestedName?: string;
  excludeAcceptAllOption?: boolean;
  types?: Array<{
    description?: string;
    accept: Record<string, string[]>;
  }>;
};

type WindowWithSaveFilePicker = Window & {
  showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
};

type RemovableFileHandle = FileSystemFileHandle & {
  remove?: () => Promise<void>;
};

export type VideoExportStorageKind = 'direct-file' | 'opfs' | 'memory';

interface BaseVideoExportArtifact {
  size: number;
  cleanup: () => Promise<void>;
}

export interface DirectFileVideoExportArtifact extends BaseVideoExportArtifact {
  blob: null;
  storageKind: 'direct-file';
}

export interface BufferedVideoExportArtifact extends BaseVideoExportArtifact {
  blob: Blob;
  storageKind: 'opfs' | 'memory';
}

export type VideoExportArtifact =
  | DirectFileVideoExportArtifact
  | BufferedVideoExportArtifact;

export interface PreparedVideoExportStorage {
  target: BufferTarget | StreamTarget;
  storageKind: VideoExportStorageKind;
  finalizeArtifact: () => Promise<VideoExportArtifact>;
  discard: () => Promise<void>;
}

export interface PrepareVideoExportStorageOptions {
  estimatedBytes: number;
  outputFileHandle?: FileSystemFileHandle | null;
  signal?: AbortSignal;
}

export function estimateVideoExportBytes(
  bitrate: number,
  durationSeconds: number
): number {
  const encodedPayload = Math.max(0, bitrate) * Math.max(0, durationSeconds) / 8;
  return Math.ceil(encodedPayload * 1.15 + 4 * MEBIBYTE);
}

export function createMemoryVideoExportArtifact(blob: Blob): VideoExportArtifact {
  return {
    blob,
    size: blob.size,
    storageKind: 'memory',
    cleanup: async () => {}
  };
}

export async function requestVideoExportFileHandle(
  fileName: string
): Promise<FileSystemFileHandle | null> {
  if (window.top !== window || navigator.userActivation?.isActive !== true) {
    return null;
  }

  const picker = (window as WindowWithSaveFilePicker).showSaveFilePicker;
  if (typeof picker !== 'function') {
    return null;
  }

  try {
    return await picker.call(window, {
      suggestedName: fileName,
      excludeAcceptAllOption: true,
      types: [
        {
          description: 'MP4 video',
          accept: {
            'video/mp4': ['.mp4']
          }
        }
      ]
    });
  } catch (error) {
    if (isVideoExportFilePickerCancellation(error)) {
      throw error;
    }
    console.warn(
      '[VideoExport] Direct file access is unavailable; using browser storage fallback.',
      error
    );
    return null;
  }
}

export function isVideoExportFilePickerCancellation(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export async function prepareVideoExportStorage(
  options: PrepareVideoExportStorageOptions
): Promise<PreparedVideoExportStorage> {
  throwIfRenderAborted(options.signal);
  const estimatedBytes = normalizeEstimatedBytes(options.estimatedBytes);

  if (options.outputFileHandle) {
    return prepareDirectFileStorage(options.outputFileHandle, options.signal);
  }

  try {
    const opfsStorage = await prepareOpfsStorage(estimatedBytes, options.signal);
    if (opfsStorage) {
      return opfsStorage;
    }
  } catch (error) {
    throwIfRenderAborted(options.signal);
    console.warn('[VideoExport] OPFS is unavailable; using bounded memory fallback.', error);
  }

  if (estimatedBytes > MAX_MEMORY_FALLBACK_BYTES) {
    throw new Error(
      'This video is too large for the browser memory fallback. Free disk space or use a browser with OPFS support.'
    );
  }

  return prepareMemoryStorage();
}

function normalizeEstimatedBytes(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.ceil(value) : 4 * MEBIBYTE;
}

async function prepareDirectFileStorage(
  fileHandle: FileSystemFileHandle,
  signal?: AbortSignal
): Promise<PreparedVideoExportStorage> {
  throwIfRenderAborted(signal);
  const writable = await fileHandle.createWritable({ keepExistingData: false });
  try {
    throwIfRenderAborted(signal);
  } catch (error) {
    try {
      await writable.abort();
    } catch {
      // The stream may already be closed after a concurrent cancellation.
    }
    throw error;
  }

  const target = new StreamTarget(
    writable as unknown as WritableStream<StreamTargetChunk>,
    {
      chunked: true,
      chunkSize: OPFS_CHUNK_SIZE
    }
  );

  let discardCompleted = false;
  const discardSelectedFile = async (): Promise<void> => {
    if (discardCompleted) {
      return;
    }

    const removableHandle = fileHandle as RemovableFileHandle;
    if (typeof removableHandle.remove === 'function') {
      try {
        await removableHandle.remove();
        discardCompleted = true;
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'NotFoundError') {
          discardCompleted = true;
          return;
        }
      }
    }

    try {
      const cleanupWritable = await fileHandle.createWritable({ keepExistingData: false });
      await cleanupWritable.close();
      discardCompleted = true;
    } catch (error) {
      console.warn('[VideoExport] Failed to clear the canceled output file.', error);
    }
  };

  return {
    target,
    storageKind: 'direct-file',
    finalizeArtifact: async () => {
      throwIfRenderAborted(signal);
      const file = await fileHandle.getFile();
      if (file.size === 0) {
        await discardSelectedFile();
        throw new Error('Video export produced an empty MP4 file.');
      }
      return {
        blob: null,
        size: file.size,
        storageKind: 'direct-file',
        cleanup: async () => {}
      };
    },
    discard: discardSelectedFile
  };
}

async function prepareOpfsStorage(
  estimatedBytes: number,
  signal?: AbortSignal
): Promise<PreparedVideoExportStorage | null> {
  const storageManager = navigator.storage as StorageManagerWithOpfs | undefined;
  if (!storageManager?.getDirectory) {
    return null;
  }

  await assertOpfsQuota(storageManager, estimatedBytes);
  throwIfRenderAborted(signal);

  const root = await storageManager.getDirectory();
  throwIfRenderAborted(signal);
  const directory = await root.getDirectoryHandle(OPFS_DIRECTORY_NAME, { create: true });
  void cleanupStaleOpfsFiles(directory);

  const fileName = createTemporaryFileName();
  let fileHandle: FileSystemFileHandle;
  let writable: FileSystemWritableFileStream | null = null;
  try {
    fileHandle = await directory.getFileHandle(fileName, { create: true });
    throwIfRenderAborted(signal);
    writable = await fileHandle.createWritable({ keepExistingData: false });
    throwIfRenderAborted(signal);
  } catch (error) {
    try {
      await writable?.abort();
    } catch {
      // The stream may already be closed after a failed or canceled creation.
    }
    try {
      await directory.removeEntry(fileName);
    } catch {
      // The entry may not have been created yet.
    }
    throw error;
  }

  const target = new StreamTarget(
    writable as unknown as WritableStream<StreamTargetChunk>,
    {
      chunked: true,
      chunkSize: OPFS_CHUNK_SIZE
    }
  );

  let removalCompleted = false;
  const removeTemporaryFile = async (): Promise<void> => {
    if (removalCompleted) {
      return;
    }
    try {
      await directory.removeEntry(fileName);
      removalCompleted = true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        removalCompleted = true;
        return;
      }
      console.warn('[VideoExport] Failed to remove temporary OPFS file.', error);
    }
  };

  return {
    target,
    storageKind: 'opfs',
    finalizeArtifact: async () => {
      throwIfRenderAborted(signal);
      const file = await fileHandle.getFile();
      if (file.size === 0) {
        await removeTemporaryFile();
        throw new Error('Video export produced an empty MP4 file.');
      }
      return {
        blob: file,
        size: file.size,
        storageKind: 'opfs',
        cleanup: removeTemporaryFile
      };
    },
    discard: removeTemporaryFile
  };
}

function prepareMemoryStorage(): PreparedVideoExportStorage {
  const target = new BufferTarget();
  return {
    target,
    storageKind: 'memory',
    finalizeArtifact: async () => {
      if (!target.buffer || target.buffer.byteLength === 0) {
        throw new Error('Video export produced an empty MP4 file.');
      }
      return createMemoryVideoExportArtifact(
        new Blob([target.buffer], { type: 'video/mp4' })
      );
    },
    discard: async () => {}
  };
}

async function assertOpfsQuota(
  storageManager: StorageManager,
  estimatedBytes: number
): Promise<void> {
  if (typeof storageManager.estimate !== 'function') {
    return;
  }

  const estimate = await storageManager.estimate();
  if (!Number.isFinite(estimate.quota) || !Number.isFinite(estimate.usage)) {
    return;
  }

  const availableBytes = Math.max(0, estimate.quota! - estimate.usage!);
  const requiredBytes = estimatedBytes + OPFS_QUOTA_RESERVE_BYTES;
  if (availableBytes < requiredBytes) {
    throw new Error('Not enough browser storage is available for this video export.');
  }
}

function createTemporaryFileName(): string {
  const id =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${OPFS_FILE_PREFIX}${id}.mp4`;
}

async function cleanupStaleOpfsFiles(directory: FileSystemDirectoryHandle): Promise<void> {
  try {
    const now = Date.now();
    for await (const [name, handle] of (directory as DirectoryHandleWithEntries).entries()) {
      if (handle.kind !== 'file' || !name.startsWith(OPFS_FILE_PREFIX)) {
        continue;
      }
      try {
        const file = await (handle as FileSystemFileHandle).getFile();
        if (now - file.lastModified > OPFS_STALE_FILE_AGE_MS) {
          await directory.removeEntry(name);
        }
      } catch {
        // A concurrent export or cleanup may have already removed the entry.
      }
    }
  } catch (error) {
    console.warn('[VideoExport] Failed to clean stale OPFS files.', error);
  }
}
